import {useContext} from 'react';
import {tokens, useTokenValue, RootLayer} from '@sail/ui';
import {
  DefaultTheme,
  DotComTheme,
  DotComThemeChroma,
  DotComThemeCottonCandy,
  DotComThemeLemonLime,
  DotComThemeOvercast,
  DotComThemePomegranate,
  DotComThemeSlate,
  DotComThemeSunburst,
  DotComThemeTropical,
  DotComThemeTwilight,
  DotComThemeWintergreen,
  LinkTheme,
  LinkAssistantTheme,
  Theme,
  ThemeProvider,
} from '@stripe-internal/conversations-ui';
import {ConfigContext} from './ConfigProvider';
import {contrastingTextColor, darkenColor} from '../helpers/colors';

type Props = {
  children: JSX.Element;
};

const defaultThemeByAudience: {[audience: string]: Theme} = {
  link: LinkTheme,
  dot_com: DotComTheme,
};

const validThemeVariantsByAudience: {
  [audience: string]: {[variant: string]: Theme};
} = {
  dot_com: {
    dot_com_Chroma: DotComThemeChroma,
    dot_com_CottonCandy: DotComThemeCottonCandy,
    dot_com_LemonLime: DotComThemeLemonLime,
    dot_com_Overcast: DotComThemeOvercast,
    dot_com_Pomegranate: DotComThemePomegranate,
    dot_com_Slate: DotComThemeSlate,
    dot_com_Sunburst: DotComThemeSunburst,
    dot_com_Tropical: DotComThemeTropical,
    dot_com_Twilight: DotComThemeTwilight,
    dot_com_Wintergreen: DotComThemeWintergreen,
  },
  link: {
    link_assistant: LinkAssistantTheme,
  },
};

export const AppThemeProvider = ({children}: Props) => {
  const config = useContext(ConfigContext);
  const {audience, primaryColor, accentColor, theme} = config;

  const lightTextColor = useTokenValue(tokens.color.neutral[100]);
  const darkTextColor = useTokenValue(tokens.color.neutral[800]);

  const themeProviderProps =
    primaryColor && accentColor
      ? {
          overrides: {
            brandColor: primaryColor,
            onBrandTextColor: contrastingTextColor(
              primaryColor,
              lightTextColor,
              darkTextColor,
            ),
            ctaColor: primaryColor,
            onCtaTextColor: contrastingTextColor(
              primaryColor,
              lightTextColor,
              darkTextColor,
            ),
            ctaHoverColor: darkenColor(primaryColor, 0.8),
            linkColor: accentColor,
            avatarGradientStart: darkenColor(primaryColor, 0.8),
            avatarGradientEnd: primaryColor,
          },
        }
      : {};

  const defaultTheme = defaultThemeByAudience[audience] || DefaultTheme;

  let themeValue = defaultTheme;

  if (!!theme && (theme.startsWith('dot_com') || theme.startsWith('link'))) {
    const validThemeVariants = validThemeVariantsByAudience[audience] || {};
    themeValue = validThemeVariants[theme] || defaultTheme;
  }

  return (
    <ThemeProvider value={themeValue} {...themeProviderProps}>
      <RootLayer>{children}</RootLayer>
    </ThemeProvider>
  );
};
